import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal } from 'antd';
import { Link } from 'react-router-dom';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import PurchasesList from '@/components/Movements/Purchases/PurchasesList';
import { AppPageHeader, Group } from '@/components/_common';
import {
  PurchaseListParams,
  PurchaseListResponse,
} from '@/core/domain/purchase/Purchase.types';
import PurchasesExtraFilters from '@/components/Movements/Purchases/PurchasesExtraFilters';
import { useEffect, useState } from 'react';
import { usePurchaseRemove } from './../../../../core/domain/purchase/Purchase.hooks';
import Authorities from '@/types/Authority';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import PurchaseDownloadAllXml from '@/components/Movements/Purchases/PurchaseDownloadAllXml';

function PurchasesReleasesPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<PurchaseListParams>({
    order: 'DESC',
    ...convertParams<PurchaseListParams>({
      code: 'string',
      documentNumber: 'string',
      releaseDate: 'string',
      search: 'string',
      supplier: 'string',
      workOrderNumber: 'string',
      companyId: 'number',
      situations: 'array-string',
      isGrouping: 'boolean',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const removeMutation = usePurchaseRemove();

  const handleRemoveClick = (purchase: PurchaseListResponse) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'purchases.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'purchases.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(purchase.id);
      },
      okType: 'danger',
    });
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      persistSearchParams={false}
      route={route.parent}
      extra={
        <Group gap={10}>
          <PurchaseDownloadAllXml />

          <Link to="/app/movements/purchases/purchasesReleases/purchasesCreate">
            <Button
              block
              type="primary"
              disabled={
                !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_ADD])
              }
            >
              <FormattedMessage id="purchases.searchBar.create" />
            </Button>
          </Link>
        </Group>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <AppPageHeader
        onSearch={(search) =>
          setQueryParams((prev) => ({ ...prev, search: search }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <PurchasesExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <PurchasesList
        filters={queryDebounce}
        handleRemoveClick={handleRemoveClick}
      />
    </DefaultRouteTabs>
  );
}

export default PurchasesReleasesPage;
